import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sprout, Upload, UserPlus } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang, LangToggle } from "@/lib/i18n";
import OtpBoxes from "@/components/OtpBoxes";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    mobile: (location.state?.mobile || "").replace(/\D/g, "").slice(0, 10),
    license_number: "",
    pos_id: "",
    address: "",
    password: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState("form");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const sendOtp = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.license_number.trim() || !form.pos_id.trim()) {
      toast.error("Name, licence number, address and POS ID are required");
      return;
    }
    if (form.mobile.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { mobile: form.mobile, purpose: "customer_register" });
      if (data.demo_otp) toast.info(`Demo mode — your OTP is ${data.demo_otp}`, { duration: 8000 });
      setStep("otp");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async () => {
    setBusy(true);
    try {
      // Create the customer account first. The returned JWT will
      // authenticate the subsequent private photo upload.
      const { data } = await api.post("/auth/register", {
        ...form,
        pos_id: form.pos_id.trim(),
        password: form.password || null,
        photo_url: null,
        otp,
      });

      login(data.token, data.user, "customer");

      // Upload the selected photo only after authentication exists.
      if (photoFile) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", photoFile);

          const uploadResponse = await api.post("/upload", fd);
          const uploadedUrl = uploadResponse.data.url;

          await api.put("/auth/profile", {
            photo_url: uploadedUrl,
          });

          setPhotoUrl(uploadedUrl);
        } catch (photoErr) {
          toast.error("Account created, but the photo could not be uploaded. You can add it from your profile.");
        } finally {
          setUploading(false);
        }
      }

      toast.success(`Welcome, ${data.user.name}! Your dealer account is ready.`);
      navigate("/customer");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#0B192C] flex items-center justify-center">
              <Sprout className="h-5 w-5 text-white" />
            </div>
            <div className="font-display font-extrabold tracking-tight text-[#0B192C]">
              SABUJ SONA FERTILIZER
              <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("brandSub")}</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link to="/" className="sf-link text-sm">
              {t("backToLogin")}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("registerTitle")}</h1>
        <p className="text-sm text-slate-500 mt-1 mb-8">{t("registerSub")}</p>

        {step === "form" ? (
          <div className="sf-card p-6 sm:p-8 grid lg:grid-cols-5 gap-8" data-testid="register-card">
            <div className="lg:col-span-3 space-y-4">
              <div>
                <label className="sf-label">{t("customerName")} *</label>
                <input data-testid="register-name-input" className="sf-input" value={form.name} onChange={set("name")} placeholder="ABC Fertilizer Store" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="sf-label">{t("mobileNumber")} *</label>
                  <div className="flex gap-2">
                    <span className="inline-flex items-center px-3 rounded-lg border border-slate-300 bg-slate-100 text-sm font-semibold text-slate-600">+91</span>
                    <input data-testid="register-mobile-input" className="sf-input" inputMode="numeric" value={form.mobile} onChange={set("mobile")} placeholder="98XXXXXXXX" />
                  </div>
                </div>
                <div>
                  <label className="sf-label">{t("licenceNumber")} *</label>
                  <input data-testid="register-license-input" className="sf-input" value={form.license_number} onChange={set("license_number")} placeholder={t("licensePh")} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="sf-label">{t("posId")} *</label>
                  <input data-testid="register-pos-input" className="sf-input" value={form.pos_id} onChange={set("pos_id")} placeholder="POS-XXXX" />
                </div>
                <div>
                  <label className="sf-label">{t("passwordOptional")}</label>
                  <input data-testid="register-password-input" type="password" className="sf-input" value={form.password} onChange={set("password")} placeholder={t("passwordPh")} />
                </div>
              </div>
              <div>
                <label className="sf-label">{t("addressLabel")} *</label>
                <textarea data-testid="register-address-input" className="sf-input min-h-24" value={form.address} onChange={set("address")} placeholder={t("addressPh")} />
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="sf-label">{t("photoLabel")}</label>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center bg-slate-50" data-testid="photo-upload-area">
                {photoPreview ? (
                  <img src={photoPreview} alt="preview" className="h-36 w-36 rounded-full object-cover mx-auto border-4 border-white shadow" />
                ) : (
                  <Upload className="h-10 w-10 text-slate-400 mx-auto" />
                )}
                <p className="text-xs text-slate-500 mt-3">{t("photoHint")}</p>
                <label className="sf-btn-outline mt-3 cursor-pointer" data-testid="photo-upload-btn">
                  {uploading ? t("uploading") : t("choosePhoto")}
                  <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
                </label>
              </div>
              <button data-testid="register-send-otp-btn" className="sf-btn-primary w-full mt-6" onClick={sendOtp} disabled={busy || uploading}>
                <UserPlus className="h-4 w-4" /> {busy ? t("sendingOtp") : t("sendOtpContinue")}
              </button>
            </div>
          </div>
        ) : (
          <div className="sf-card p-8 max-w-lg mx-auto text-center" data-testid="register-otp-card">
            <h2 className="font-display text-2xl font-bold text-slate-900">{t("verifyMobile")}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              {t("otpSentTo")} <span className="font-semibold text-slate-800">+91 {form.mobile}</span>
            </p>
            <div className="flex justify-center">
              <OtpBoxes value={otp} onChange={setOtp} testid="register-otp" />
            </div>
            <button data-testid="register-create-btn" className="sf-btn-primary w-full mt-6" onClick={createAccount} disabled={busy || otp.length !== 6}>
              {busy ? t("creatingAccount") : t("createAccount")}
            </button>
            <div className="flex justify-between mt-4 text-sm">
              <button className="sf-link" onClick={() => setStep("form")}>
                {t("back")}
              </button>
              <button className="sf-link" onClick={sendOtp}>
                {t("resendOtp")}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
